import { ReactComponent as SeverityIcon } from '@elementary/common-ui/assets/16x16/severity.svg';
import { ReactComponent as StatusIcon } from '@elementary/common-ui/assets/20x20/incident-open.svg';
import { ReactComponent as MergeIcon } from '@elementary/common-ui/assets/20x20/merge.svg';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/24x24/user.svg';
import {
  ActionsMenuButton,
  DropDown,
  FloatingActionsMenu,
  SvgIcon,
} from '@elementary/common-ui/components';
import Menu, { Option } from '@elementary/common-ui/components/Menu/Menu';
import styled from 'styled-components';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import { IncidentDetails } from 'incidents/types';

const BlackStatusIcon = styled.div`
  display: inline-flex;

  svg path[stroke] {
    stroke: ${({ theme }) => theme.palette.common.black};
  }

  svg path[fill]:not([fill='none']) {
    fill: ${({ theme }) => theme.palette.common.black};
  }
`;

type Props = {
  selectedCount: number;
  severityOptions?: Option[];
  statusOptions?: Option[];
  assigneeOptions?: Option[];
  onChange: (val: Partial<IncidentDetails>) => void;
  onClose: () => void;
  onMerge?: () => void;
};

const IncidentsFloatingActionMenu = ({
  selectedCount,
  severityOptions,
  statusOptions,
  assigneeOptions,
  onChange,
  onClose,
  onMerge,
}: Props) => {
  const canMerge = selectedCount >= 2;

  const actions = [
    {
      value: 'severity',
      itemRenderer: () => (
        <Menu
          menuButton={
            <ActionsMenuButton
              label="Severity"
              icon={
                <SvgIcon fillColor="black">
                  <SeverityIcon width={20} />
                </SvgIcon>
              }
            />
          }
          anchorOrigin={{ vertical: -234, horizontal: 'left' }}
          menuTitle="SET SEVERITY"
          options={severityOptions}
          closeOnChange
          onChange={(val) =>
            onChange({ severity: val as IncidentDetails['severity'] })
          }
        />
      ),
    },

    {
      value: 'assignee',
      itemRenderer: () => (
        <DropDown
          menuButton={
            <ActionsMenuButton
              label="Assignee"
              icon={
                <SvgIcon fillColor="black">
                  <UserIcon width={22} />
                </SvgIcon>
              }
            />
          }
          showClear
          showSearch
          sortOptions={false}
          options={assigneeOptions || []}
          MenuProps={{ anchorOrigin: { vertical: -330, horizontal: 'center' } }}
          onChange={(val) => {
            onChange({
              assignee:
                val === NO_ASSIGNEE_VALUE
                  ? NO_ASSIGNEE_VALUE
                  : assigneeOptions?.find((o) => o.value === val)?.value ||
                    NO_ASSIGNEE_VALUE,
            });
          }}
        />
      ),
    },

    {
      value: 'status',
      itemRenderer: () => (
        <Menu
          menuButton={
            <ActionsMenuButton
              label="Status"
              icon={
                <BlackStatusIcon>
                  <StatusIcon width={22} />
                </BlackStatusIcon>
              }
            />
          }
          anchorOrigin={{ vertical: -190, horizontal: 'left' }}
          menuTitle="SET STATUS"
          options={statusOptions}
          closeOnChange
          onChange={(val) =>
            onChange({
              status: val as IncidentDetails['status'],
            })
          }
        />
      ),
    },
    ...(onMerge
      ? [
          {
            value: 'merge',
            itemRenderer: () => (
              <ActionsMenuButton
                label="Merge"
                icon={
                  <SvgIcon fillColor="black">
                    <MergeIcon width={20} height={20} />
                  </SvgIcon>
                }
                onClick={onMerge}
                disabled={!canMerge}
                tooltip={
                  canMerge ? 'Merge' : 'Select at least 2 incidents to merge'
                }
              />
            ),
          },
        ]
      : []),
  ];

  return (
    <FloatingActionsMenu
      itemCount={selectedCount}
      countLabel="Incident"
      countLabelPlural="Incidents"
      actions={actions}
      onClose={onClose}
    />
  );
};

export default IncidentsFloatingActionMenu;
